import { useState, useMemo, useEffect, useRef } from 'react'
import { SWIPE_TAP_THRESHOLD_PX } from '@/shared/constants/gestures'
import { SLIDER_AUTO_LOOP_DELAY_MS } from '@/shared/constants/timings'
export { useIsMobile } from '@/shared/hooks/use-is-mobile'

interface UseSliderOptions {
  autoLoop?: boolean
  delay?: number
  itemsPerSlide?: number
  onSlideChange?: (activeSlide: number) => void
}

export function useSlider<T>(
  items: T[],
  {
    autoLoop = false,
    delay = SLIDER_AUTO_LOOP_DELAY_MS,
    itemsPerSlide = 6,
    onSlideChange,
  }: UseSliderOptions = {},
) {
  const [currentSlide, setCurrentSlide] = useState(0)
  // when false the CSS transition is removed so the instant reset to 0 is invisible
  const [animated, setAnimated] = useState(true)

  const slides = useMemo(() => {
    const result: T[][] = []
    for (let i = 0; i < items.length; i += itemsPerSlide) {
      result.push(items.slice(i, i + itemsPerSlide))
    }
    return result
  }, [items, itemsPerSlide])

  // clamp currentSlide when itemsPerSlide changes (e.g. viewport resize)
  useEffect(() => {
    setCurrentSlide((prev) => Math.min(prev, Math.max(0, slides.length - 1)))
  }, [slides.length])

  const totalSlides = slides.length

  useEffect(() => {
    if (!autoLoop || totalSlides <= 1) return
    const id = setInterval(() => {
      // allow going to totalSlides, which is the clone of slide 0
      setCurrentSlide((prev) => (prev >= totalSlides ? prev : prev + 1))
    }, delay)
    return () => clearInterval(id)
  }, [autoLoop, delay, totalSlides])

  // called by the component when the CSS transition ends
  const handleTransitionEnd = () => {
    if (currentSlide === totalSlides) {
      // we just landed on the clone — jump back to the real slide 0, no animation
      setAnimated(false)
      setCurrentSlide(0)
    }
  }

  // re-enable animation after the invisible reset (needs two rAFs to force a reflow)
  useEffect(() => {
    if (animated) return
    let inner: number = 0
    const outer: number = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setAnimated(true))
    })
    return () => {
      cancelAnimationFrame(outer)
      cancelAnimationFrame(inner)
    }
  }, [animated])

  const goToSlide = (index: number) => {
    setAnimated(true)
    setCurrentSlide(index)
  }

  const touchStartX = useRef<number | undefined>(undefined)

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? undefined
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === undefined || totalSlides <= 1) return
    const delta = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current
    touchStartX.current = undefined
    if (Math.abs(delta) < SWIPE_TAP_THRESHOLD_PX) return
    if (delta < 0) {
      // swipe left → next
      setAnimated(true)
      setCurrentSlide((prev) => (prev >= totalSlides ? prev : prev + 1))
    } else {
      // swipe right → previous
      setAnimated(true)
      setCurrentSlide((prev) => Math.max(0, prev - 1))
    }
  }

  // for dot highlighting: clone position maps back to slide 0
  const activeSlide = currentSlide % (totalSlides || 1)

  useEffect(() => {
    onSlideChange?.(activeSlide)
  }, [activeSlide])

  return {
    slides,
    currentSlide,
    activeSlide,
    totalSlides,
    goToSlide,
    animated,
    handleTransitionEnd,
    handleTouchStart,
    handleTouchEnd,
  }
}
